import React, { useEffect, useMemo, useState } from "react";
import SharedLayout from "./SharedLayout";
import { FiChevronDown } from "react-icons/fi";
import apiClient from "./services/apiClient";
import { useParams } from "react-router-dom";
import InfoModal from "./modals/InfoModals";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL; // Removed

const ProgressCircle = ({ value = 0, size = 72, stroke = 6, label, sub }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const r = size / 2 - stroke / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#e6e6e6"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#07183c"
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset .5s ease" }}
        />
        <text
          x="50%"
          y="50%"
          dy=".3em"
          textAnchor="middle"
          fontWeight="700"
          fontSize={size / 5}
        >
          %{pct}
        </text>
      </svg>
      {label && <span className="mt-2 text-xs sm:text-sm">{label}</span>}
      {sub && <span className="text-[10px] text-gray-500 mt-0.5">{sub}</span>}
    </div>
  );
};

const Select = ({ children, value, onChange }) => (
  <div className="relative w-full sm:w-64">
    <select
      value={value}
      onChange={onChange}
      className="appearance-none w-full pl-4 pr-10 py-2 rounded-full border border-gray-300
                 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
    >
      {children}
    </select>
    <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" />
  </div>
);

// Küçük "i" tooltip
const InfoTooltip = ({ text }) => (
  <span className="relative inline-block group ml-2 align-middle">
    <span className="w-4 h-4 rounded-full border border-gray-400 text-[10px] flex items-center justify-center cursor-default">
      i
    </span>
    <span className="absolute left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block z-10 bg-gray-900 text-white text-[10px] leading-snug px-2 py-1 rounded shadow-lg whitespace-nowrap">
      {text}
    </span>
  </span>
);

// JSON → Excel helper
const exportToExcel = (headers, rows, fileName) => {
  const sheetData = [
    headers,
    ...rows.map((row) => headers.map((header) => row[header] ?? "")),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });

  saveAs(blob, `${fileName}.xlsx`);
};

const InsightsPage = () => {
  const { userId } = useParams();

  // state
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [courseLessons, setCourseLessons] = useState([]);
  const [selectedLessonIdx, setSelectedLessonIdx] = useState("");
  const [learnerInsights, setLearnerInsights] = useState([]);
  const [deliveryRows, setDeliveryRows] = useState([]);
  const [instantMessageRows, setInstantMessageRows] = useState([]);

  // modals
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const openInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  // fetch insights
  useEffect(() => {
    const fetchInsights = async () => {
      try {
        // v2: tenant-based insights endpoint
        const data = await apiClient.get("/learners/insights");

        if (data?.insights) {
          setLearnerInsights(data.insights);
        }
      } catch (e) {
        console.error(e);
        openInfo(
          "error",
          "Yükleme Hatası",
          e.formatted?.message || "Ders analizi alınamadı.",
        );
      }
    };
    fetchInsights();
  }, [userId]);

  // fetch courses
  useEffect(() => {
    const run = async () => {
      if (!userId) return;
      try {
        // v2: tenant-based admin courses endpoint
        const data = await apiClient.get("/courses/admin");

        const list = Array.isArray(data) ? data : data?.courses || [];

        const formatted = list
          .filter((c) => c.status === "PUBLISHED")
          .map((c) => ({
            id: c.id,
            name: c.name,
            totalEnrollments: c.totalEnrollments,
            averageProgress: Number(c.averageProgress || 0),
            status: c.status,
          }));
        setCourses(formatted);
        if (formatted.length) setSelectedCourseId(String(formatted[0].id));
      } catch (e) {
        console.error(e);
        openInfo(
          "error",
          "Yükleme Hatası",
          e.formatted?.message || "Kurs listesi alınamadı.",
        );
      }
    };
    run();
  }, [userId]);

  // fetch lessons for selected course
  useEffect(() => {
    const run = async () => {
      if (!selectedCourseId) return;
      try {
        // apiClient unwrap - /courses/:id
        const data = await apiClient.get(`/courses/${selectedCourseId}`);

        setCourseLessons(Array.isArray(data?.lessons) ? data.lessons : []);
        setSelectedLessonIdx(data?.lessons?.length ? "0" : "");
      } catch (e) {
        console.error(e);
        openInfo(
          "error",
          "Yükleme Hatası",
          e.formatted?.message || "Ders listesi alınamadı.",
        );
        setCourseLessons([]);
        setSelectedLessonIdx("");
      }
    };
    run();
  }, [selectedCourseId]);

  useEffect(() => {
    const run = async () => {
      if (!selectedCourseId) {
        setDeliveryRows([]);
        return;
      }

      try {
        const data = await apiClient.get(
          `/courses/${selectedCourseId}/delivery-debug`,
        );
        setDeliveryRows(Array.isArray(data?.rows) ? data.rows : []);
      } catch (e) {
        console.error(e);
        setDeliveryRows([]);
      }
    };

    run();
  }, [selectedCourseId]);

  useEffect(() => {
    const run = async () => {
      try {
        const data = await apiClient.get("/reports/instant-messages");
        setInstantMessageRows(Array.isArray(data?.rows) ? data.rows : []);
      } catch (e) {
        console.error(e);
        setInstantMessageRows([]);
      }
    };

    run();
  }, []);

  const selectedCourse = useMemo(
    () => courses.find((c) => String(c.id) === String(selectedCourseId)),
    [courses, selectedCourseId],
  );

  const learnersCount = selectedCourse?.totalEnrollments ?? 0;
  const selectedCourseIdNum = selectedCourseId
    ? Number(selectedCourseId)
    : null;

  const deliveryChannelSummary = useMemo(() => {
    let whatsapp = 0;
    let teams = 0;

    deliveryRows.forEach((row) => {
      const channel = String(
        row.channel || row.deliveryChannel || "whatsapp",
      ).toLowerCase();
      if (channel === "teams") teams += 1;
      else whatsapp += 1;
    });

    return { whatsapp, teams };
  }, [deliveryRows]);

  // --------- EĞİTİM (KURS) ÖLÇÜMLERİ – Başlamadı / Tamamlandı / Devam Ediyor
  const courseStats = useMemo(() => {
    if (!selectedCourseIdNum) {
      return {
        notStarted: 0,
        completed: 0,
        inProgress: 0,
        pctNotStarted: 0,
        pctCompleted: 0,
        pctInProgress: 0,
      };
    }

    let completed = 0;
    let inProgress = 0;
    let notStarted = 0;

    learnerInsights.forEach((ins) => {
      const enrolledCourseIds = ins.enrolledCourseIds || [];
      const isEnrolled = enrolledCourseIds.includes(selectedCourseIdNum);
      if (!isEnrolled) return;

      const cp = ins.recentActivity.courseProgress.find(
        (cp) => Number(cp.courseId) === selectedCourseIdNum,
      );

      if (!cp) {
        notStarted++;
      } else if (
        cp.isCompleted ||
        cp.completedAt ||
        (cp.progressPercent || 0) >= 100
      ) {
        completed++;
      } else {
        inProgress++;
      }
    });

    const total = completed + inProgress + notStarted;
    const denom = learnersCount || total;
    const pct = (n) => (denom > 0 ? Math.round((n / denom) * 100) : 0);

    return {
      notStarted,
      completed,
      inProgress,
      pctNotStarted: pct(notStarted),
      pctCompleted: pct(completed),
      pctInProgress: pct(inProgress),
      totalEnrolled: denom,
    };
  }, [learnerInsights, selectedCourseIdNum, learnersCount]);

  const selectedLesson =
    selectedLessonIdx !== "" ? courseLessons[Number(selectedLessonIdx)] : null;

  // --------- KONU (DERS) ÖLÇÜMLERİ – Başlamadı / Tamamlandı / Devam Ediyor
  const lessonStats = useMemo(() => {
    if (!selectedLesson || !selectedCourseIdNum) {
      return {
        notStarted: 0,
        completed: 0,
        inProgress: 0,
        pctNotStarted: 0,
        pctCompleted: 0,
        pctInProgress: 0,
      };
    }

    let completed = 0;
    let inProgress = 0;
    let notStarted = 0;

    learnerInsights.forEach((ins) => {
      const enrolledCourseIds = ins.enrolledCourseIds || [];
      const isEnrolled = enrolledCourseIds.includes(selectedCourseIdNum);
      if (!isEnrolled) return;

      const lp = ins.recentActivity.lessonProgress.find(
        (lp) => lp.lessonId === selectedLesson.id,
      );

      if (!lp) {
        notStarted++;
      } else if (lp.isCompleted || lp.completedAt) {
        completed++;
      } else {
        inProgress++;
      }
    });

    const total = completed + inProgress + notStarted;
    const denom = learnersCount || total;
    const pct = (n) => (denom > 0 ? Math.round((n / denom) * 100) : 0);

    return {
      notStarted,
      completed,
      inProgress,
      pctNotStarted: pct(notStarted),
      pctCompleted: pct(completed),
      pctInProgress: pct(inProgress),
      totalEnrolled: denom,
    };
  }, [learnerInsights, selectedLesson, selectedCourseIdNum, learnersCount]);

  const selectedCourseLearners = useMemo(() => {
    if (!selectedCourseIdNum) return [];

    return learnerInsights.filter((ins) =>
      (ins.enrolledCourseIds || []).includes(selectedCourseIdNum),
    );
  }, [learnerInsights, selectedCourseIdNum]);

  const normalizePhone = (value = "") => String(value).replace(/\D/g, "");

  const exportInstantMessageReport = () => {
    const headers = [
      "MesajTipi",
      "HedefTipi",
      "Kanal",
      "Grup",
      "Kurs",
      "Katılımcı",
      "TelefonNo",
      "DersId",
      "İçerikTipi",
      "İçerik",
      "MesajId",
      "Durum",
      "GönderimTarihi",
      "OkunmaTarihi",
    ];

    const rows = instantMessageRows.map((row) => ({
      MesajTipi: row.runType || "-",
      HedefTipi: row.targetType || "-",
      Grup: row.groupName || "-",
      Kurs: row.courseName || "-",
      Katılımcı: row.recipientName || "-",
      TelefonNo: row.phoneNumber || "-",
      DersId: row.lessonId ?? "-",
      İçerikTipi: row.contentType || "-",
      İçerik: row.contentValue || "-",
      MesajId: row.messageId || "-",
      Durum: row.messageStatus || "queued",
      GönderimTarihi: row.sentAt
        ? new Date(row.sentAt).toLocaleString("tr-TR")
        : "-",
      Kanal: String(row.channel || row.deliveryChannel || "whatsapp").replace(
        /^./,
        (c) => c.toUpperCase(),
      ),
      OkunmaTarihi: row.readAt
        ? new Date(row.readAt).toLocaleString("tr-TR")
        : "-",
    }));

    exportToExcel(headers, rows, "Anlik_Mesaj_Raporu");
  };

  const formatDate = (value, withTime = false) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return withTime ? d.toLocaleString("tr-TR") : d.toLocaleDateString("tr-TR");
  };

  const getDeliveryChannelLabel = (delivery) => {
    const raw = String(
      delivery?.channel || delivery?.deliveryChannel || "whatsapp",
    ).toLowerCase();

    if (raw === "teams") return "Teams";
    return "WhatsApp";
  };

  const deliveryByLearnerLesson = useMemo(() => {
    const map = new Map();

    deliveryRows.forEach((row) => {
      const key = `${normalizePhone(row.learnerNumber)}_${row.lessonId}`;
      const prev = map.get(key);

      if (!prev) {
        map.set(key, row);
        return;
      }

      const prevTs = new Date(
        prev.updatedAt ||
          prev.readAt ||
          prev.deliveredAt ||
          prev.apiSentAt ||
          0,
      ).getTime();

      const currTs = new Date(
        row.updatedAt || row.readAt || row.deliveredAt || row.apiSentAt || 0,
      ).getTime();

      if (currTs >= prevTs) {
        map.set(key, row);
      }
    });

    return map;
  }, [deliveryRows]);

  const getDeliveryFor = (phoneNumber, lessonId) => {
    return (
      deliveryByLearnerLesson.get(
        `${normalizePhone(phoneNumber)}_${lessonId}`,
      ) || null
    );
  };

  const getCourseProgressForSelectedCourse = (ins) => {
    return ins.recentActivity.courseProgress.find(
      (cp) => Number(cp.courseId) === Number(selectedCourseIdNum),
    );
  };

  const getLessonProgressFor = (ins, lessonId) => {
    return ins.recentActivity.lessonProgress.find(
      (lp) => Number(lp.lessonId) === Number(lessonId),
    );
  };

  const hasMessageSent = (delivery) =>
    Boolean(
      delivery?.apiSentAt ||
      delivery?.deliveredAt ||
      delivery?.readAt ||
      delivery?.completedAt ||
      ["api_sent", "delivered", "read", "completed"].includes(delivery?.status),
    );

  const hasMessageRead = (delivery) =>
    Boolean(
      delivery?.readAt || ["read", "completed"].includes(delivery?.status),
    );

  const getLessonCompletionStatus = (lp, delivery) => {
    if (
      lp?.isCompleted ||
      lp?.completedAt ||
      delivery?.completedAt ||
      delivery?.status === "completed"
    ) {
      return "tamamlandı";
    }
    if (lp || hasMessageSent(delivery)) {
      return "devam ediyor";
    }
    return "başlamadı";
  };

  const getActivityStatus = (lp, delivery) => {
    if (
      lp?.isCompleted ||
      lp?.completedAt ||
      delivery?.completedAt ||
      delivery?.status === "completed"
    ) {
      return "tamamlandı";
    }
    if (hasMessageRead(delivery)) return "okundu";
    if (hasMessageSent(delivery)) return "iletildi";
    return "başlamadı";
  };

  const parseQuizQuestions = (quiz) => {
    if (!quiz) return [];

    if (Array.isArray(quiz.questions)) {
      return quiz.questions;
    }

    if (typeof quiz.questions === "string") {
      try {
        const parsed = JSON.parse(quiz.questions);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {}
    }

    if (quiz.question) {
      return [
        {
          text: quiz.question,
          correctOption: quiz.correctOption || null,
        },
      ];
    }

    return [];
  };

  const getQuizQuestionText = (lesson) => {
    const questions = parseQuizQuestions(lesson?.quiz);
    if (!questions.length) return "-";

    return questions
      .map((q, idx) => `${idx + 1}. ${q?.text || q?.question || "-"}`)
      .join(" | ");
  };

  const getQuizCorrectAnswer = (lesson) => {
    const questions = parseQuizQuestions(lesson?.quiz);
    if (!questions.length) return lesson?.quiz?.correctOption || "-";

    return questions.map((q) => q?.correctOption || "-").join(" | ");
  };

  // RAPOR EXPORT FONKSİYONLARI
  const exportLearnerReport = () => {
    const headers = [
      "Ad",
      "Soyad",
      "TelefonNo",
      "KatılımTarihi",
      "ToplamKursSayısı",
      "TamamlananKurs",
      "DevamEdenKurs",
      "BaşlamayanKurs",
      "TamamlamanDersSayısı",
      "Tamamlama Yüzdesi",
    ];

    const rows = selectedCourseLearners.map((ins) => {
      const cp = getCourseProgressForSelectedCourse(ins);
      const completionPct = Math.round(Number(cp?.progressPercent || 0));

      return {
        Ad: ins.learner.name,
        Soyad: ins.learner.surname,
        TelefonNo: ins.learner.number,
        KatılımTarihi: formatDate(ins.learner.joinedAt),
        ToplamKursSayısı: ins.statistics.totalCourses,
        TamamlananKurs: ins.statistics.completedCourses,
        DevamEdenKurs: ins.statistics.inProgressCourses,
        BaşlamayanKurs: ins.statistics.notStartedCourses,
        TamamlamanDersSayısı: ins.statistics.totalLessonsCompleted,
        "Tamamlama Yüzdesi": `%${completionPct}`,
      };
    });

    exportToExcel(headers, rows, "Kullanici_Raporu");
  };

  const exportCourseReport = () => {
    if (!selectedCourse || courseLessons.length === 0) return;

    const headers = [
      "KursAdı",
      "KayıtlıKatılımcıSayısı",
      "OrtalamaİlerlemeYüzdesi",
      "KursDurumu",
      "Ders",
      "Tamamlandı",
      "Devam Ediyor",
      "Başlamadı",
      "Mesaj İletildi",
      "Mesaj Okundu",
    ];

    const rows = courseLessons.map((lesson) => {
      let completed = 0;
      let inProgress = 0;
      let notStarted = 0;
      let sent = 0;
      let read = 0;

      selectedCourseLearners.forEach((ins) => {
        const lp = getLessonProgressFor(ins, lesson.id);
        const delivery = getDeliveryFor(ins.learner.number, lesson.id);

        if (hasMessageSent(delivery)) sent += 1;
        if (hasMessageRead(delivery)) read += 1;

        const status = getLessonCompletionStatus(lp, delivery);

        if (status === "tamamlandı") completed += 1;
        else if (status === "devam ediyor") inProgress += 1;
        else notStarted += 1;
      });

      return {
        KursAdı: selectedCourse.name,
        KayıtlıKatılımcıSayısı: selectedCourse.totalEnrollments,
        OrtalamaİlerlemeYüzdesi: `%${Math.round(
          Math.min(100, Number(selectedCourse.averageProgress || 0)),
        )}`,
        KursDurumu: selectedCourse.status,
        Ders: lesson.title,
        Tamamlandı: completed,
        "Devam Ediyor": inProgress,
        Başlamadı: notStarted,
        "Mesaj İletildi": sent,
        "Mesaj Okundu": read,
      };
    });

    exportToExcel(headers, rows, "Kurs_Raporu");
  };

  const exportLessonReport = () => {
    if (!selectedCourseIdNum || courseLessons.length === 0) {
      openInfo("warning", "Uyarı", "Lütfen dersleri olan bir eğitim seçiniz.");
      return;
    }

    const headers = [
      "Katılımcı",
      "DersBaşlık",
      "Kanal",
      "Tamamlama Puanı",
      "TamamlanmaTarihi",
      "Tamamlama Durumu",
      "Mesaj İletildi",
      "Mesaj Okundu",
    ];

    const rows = [];

    selectedCourseLearners.forEach((ins) => {
      courseLessons.forEach((lesson) => {
        const lp = getLessonProgressFor(ins, lesson.id);
        const delivery = getDeliveryFor(ins.learner.number, lesson.id);

        rows.push({
          Katılımcı: `${ins.learner.name} ${ins.learner.surname}`,
          DersBaşlık: lesson.title,
          "Tamamlama Puanı": lp?.quizScore ?? "-",
          TamamlanmaTarihi: formatDate(
            lp?.completedAt || delivery?.completedAt,
          ),
          Kanal: getDeliveryChannelLabel(delivery),
          "Tamamlama Durumu": getLessonCompletionStatus(lp, delivery),
          "Mesaj İletildi": hasMessageSent(delivery) ? "Evet" : "Hayır",
          "Mesaj Okundu": hasMessageRead(delivery) ? "Evet" : "Hayır",
        });
      });
    });

    exportToExcel(headers, rows, "Ders_Raporu");
  };

  const exportActivityReport = () => {
    if (!selectedCourseIdNum) {
      openInfo("warning", "Uyarı", "Lütfen bir eğitim seçiniz.");
      return;
    }

    const headers = [
      "Katılımcı",
      "Kurs",
      "Ders",
      "Kanal",
      "Mesaj",
      "Durum",
      "İşlemTarihi",
      "DoğruCevap",
      "KullanıcıCevabı",
      "QuizSkoru",
    ];

    const rows = [];

    selectedCourseLearners.forEach((ins) => {
      courseLessons.forEach((lesson) => {
        if (!lesson.quiz) return;

        const lp = getLessonProgressFor(ins, lesson.id);
        const delivery = getDeliveryFor(ins.learner.number, lesson.id);

        rows.push({
          Katılımcı: `${ins.learner.name} ${ins.learner.surname}`,
          Kurs: selectedCourse?.name || "-",
          Ders: lesson.title,
          Mesaj: getQuizQuestionText(lesson),
          Durum: getActivityStatus(lp, delivery),
          İşlemTarihi: formatDate(
            lp?.completedAt ||
              delivery?.completedAt ||
              delivery?.readAt ||
              delivery?.deliveredAt ||
              delivery?.apiSentAt,
            true,
          ),
          DoğruCevap: getQuizCorrectAnswer(lesson),
          KullanıcıCevabı: lp?.quizReply ?? "-",
          QuizSkoru: lp?.quizScore ?? "-",
        });
      });
    });

    if (rows.length === 0) {
      openInfo(
        "info",
        "Veri Yok",
        "Seçili eğitime ait quiz aktivitesi bulunamadı.",
      );
      return;
    }

    exportToExcel(headers, rows, "Aktivite_Raporu");
  };

  return (
    <SharedLayout pageTitle="RAPORLAR">
      <div className="bg-gray-50 p-8 rounded-2xl shadow-sm">
        <div className="max-w-[1400px] mx-auto">
          {/* ÜST FİLTRE: KURS */}
          <div className="mb-6 sm:mb-8">
            <Select
              value={selectedCourseId}
              onChange={(e) => setSelectedCourseId(e.target.value)}
            >
              {courses.length === 0 && <option value="">KURS YOK</option>}
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>

          {/* EXPORT BUTTONS */}
          <div className="flex flex-wrap gap-3 mb-8">
            <button
              onClick={exportLearnerReport}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Kullanıcı Raporu
            </button>
            <button
              onClick={exportCourseReport}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Kurs Raporu
            </button>
            <button
              onClick={exportLessonReport}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Ders Raporu
            </button>
            <button
              onClick={exportActivityReport}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
            >
              Aktivite Raporu
            </button>
            <button
              onClick={exportInstantMessageReport}
              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
            >
              Anlık Mesaj Raporu
            </button>
          </div>

          {/* ÜST SIRA */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            <div className="col-span-1 bg-white rounded-xl shadow-sm p-5 sm:p-6 flex flex-col justify-between min-h-[160px]">
              <div>
                <h4 className="text-green-600 font-medium mb-2">
                  KATILIMCILAR
                </h4>
                <p className="text-3xl sm:text-4xl font-bold">
                  {learnersCount}
                </p>
              </div>
            </div>

            <div className="col-span-1 md:col-span-2 bg-white rounded-xl shadow-sm p-5 sm:p-6">
              <h4 className="text-green-600 font-medium mb-4 sm:mb-5 flex items-center">
                EĞİTİM ÖLÇÜMLERİ
                <InfoTooltip text="Seçili eğitime kayıtlı katılımcıların durum dağılımı: Başlamadı, Tamamlandı, Devam Ediyor." />
              </h4>
              <div className="grid grid-cols-1 min-[520px]:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
                <ProgressCircle
                  value={courseStats.pctNotStarted}
                  size={84}
                  stroke={7}
                  label="BAŞLAMADI"
                  sub={`${courseStats.notStarted}/${
                    courseStats.totalEnrolled || learnersCount
                  }`}
                />

                <ProgressCircle
                  value={courseStats.pctCompleted}
                  size={84}
                  stroke={7}
                  label="TAMAMLANDI"
                  sub={`${courseStats.completed}/${
                    courseStats.totalEnrolled || learnersCount
                  }`}
                />

                <ProgressCircle
                  value={courseStats.pctInProgress}
                  size={84}
                  stroke={7}
                  label="DEVAM EDİYOR"
                  sub={`${courseStats.inProgress}/${
                    courseStats.totalEnrolled || learnersCount
                  }`}
                />
              </div>
            </div>

            <div className="col-span-1 bg-white rounded-xl shadow-sm p-5 sm:p-6 flex flex-col justify-between min-h-[160px]">
              <div>
                <h4 className="text-blue-600 font-medium mb-2">
                  TESLİMAT KANALLARI
                </h4>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-center justify-between">
                    <span>WhatsApp</span>
                    <span className="font-semibold">
                      {deliveryChannelSummary.whatsapp}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Teams</span>
                    <span className="font-semibold">
                      {deliveryChannelSummary.teams}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ALT FİLTRE: DERSLER */}
          <div className="mt-8 mb-6">
            <Select
              value={selectedLessonIdx}
              onChange={(e) => setSelectedLessonIdx(e.target.value)}
            >
              {courseLessons.length === 0 && <option value="">DERS YOK</option>}
              {courseLessons.map((l, idx) => (
                <option key={idx} value={idx}>
                  {l.title || `LESSON ${idx + 1}`}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3">
            <div className="col-span-1 bg-white rounded-xl shadow-sm p-5 sm:p-6">
              <h4 className="text-green-600 font-medium mb-4 flex items-center">
                KONU ÖLÇÜMLERİ
                <InfoTooltip text="Seçili ders özelinde katılımcıların durum dağılımı: Başlamadı, Tamamlandı, Devam Ediyor." />
              </h4>
              <div className="grid grid-cols-1 min-[520px]:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
                <ProgressCircle
                  value={lessonStats.pctNotStarted}
                  size={80}
                  stroke={6}
                  label="BAŞLAMADI"
                  sub={`${lessonStats.notStarted}/${
                    lessonStats.totalEnrolled || learnersCount
                  }`}
                />

                <ProgressCircle
                  value={lessonStats.pctCompleted}
                  size={80}
                  stroke={6}
                  label="TAMAMLANDI"
                  sub={`${lessonStats.completed}/${
                    lessonStats.totalEnrolled || learnersCount
                  }`}
                />

                <ProgressCircle
                  value={lessonStats.pctInProgress}
                  size={80}
                  stroke={6}
                  label="DEVAM EDİYOR"
                  sub={`${lessonStats.inProgress}/${
                    lessonStats.totalEnrolled || learnersCount
                  }`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={closeInfo}
      />
    </SharedLayout>
  );
};

export default InsightsPage;
